import { useEffect, useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import { CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { api, type ApiError } from '../lib/api';
import { pushToast } from '../lib/toast';

interface GmailStatus {
  connected: boolean;
  gmailAddress: string | null;
  updatedAt: string | null;
}

interface NaverCredentials {
  clientId: string;
  hasSecret: boolean;
}

interface CoupangCredentials {
  accessKey: string;
  vendorId: string;
  hasSecret: boolean;
}

interface AllowedEmails {
  emails: string[];
}

interface LogisticsEmail {
  email: string;
}

interface DefaultCarrier {
  code: string;
  options: ReadonlyArray<{ code: string; label: string }>;
}

interface SettingsData {
  gmail: GmailStatus;
  naver: NaverCredentials;
  coupang: CoupangCredentials;
  allowedEmails: AllowedEmails;
  logisticsEmail: LogisticsEmail;
  defaultCarrier: DefaultCarrier;
}

export async function settingsLoader(): Promise<SettingsData> {
  const [gmail, naver, coupang, allowedEmails, logisticsEmail, defaultCarrier] = await Promise.all([
    api.get<GmailStatus>('/settings/gmail-status'),
    api.get<NaverCredentials>('/settings/naver-credentials'),
    api.get<CoupangCredentials>('/settings/coupang-credentials'),
    api.get<AllowedEmails>('/settings/allowed-emails'),
    api.get<LogisticsEmail>('/settings/logistics-email'),
    api.get<DefaultCarrier>('/settings/default-carrier'),
  ]);
  return { gmail, naver, coupang, allowedEmails, logisticsEmail, defaultCarrier };
}

const ERROR_MESSAGES: Record<string, string> = {
  state_mismatch: '보안 검증에 실패했습니다. 다시 시도해 주세요.',
  no_refresh_token: '오프라인 접근 권한을 얻지 못했습니다. 브라우저에서 Google 계정 접근 권한을 초기화한 뒤 다시 시도해 주세요.',
  token_exchange: '토큰 교환에 실패했습니다. 잠시 후 다시 시도해 주세요.',
  missing_code: '인증 코드를 받지 못했습니다. 다시 시도해 주세요.',
};

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Seoul',
  }).format(new Date(iso));
}

export function SettingsPage() {
  const data = useLoaderData() as SettingsData;
  const [searchParams, setSearchParams] = useSearchParams();

  // Naver credentials state
  const [naverClientId, setNaverClientId] = useState(data.naver.clientId);
  const [naverClientSecret, setNaverClientSecret] = useState('');
  const [naverSaving, setNaverSaving] = useState(false);

  // Coupang credentials state
  const [coupangAccessKey, setCoupangAccessKey] = useState(data.coupang.accessKey);
  const [coupangSecretKey, setCoupangSecretKey] = useState('');
  const [coupangVendorId, setCoupangVendorId] = useState(data.coupang.vendorId);
  const [coupangSaving, setCoupangSaving] = useState(false);

  // Allowed emails state
  const [emailsText, setEmailsText] = useState(data.allowedEmails.emails.join('\n'));
  const [emailsSaving, setEmailsSaving] = useState(false);

  // Logistics email state
  const [logisticsEmail, setLogisticsEmail] = useState(data.logisticsEmail.email);
  const [logisticsSaving, setLogisticsSaving] = useState(false);

  // Default carrier state
  const [carrierCode, setCarrierCode] = useState(data.defaultCarrier.code);
  const [carrierSaving, setCarrierSaving] = useState(false);

  useEffect(() => {
    const gmail = searchParams.get('gmail');
    const reason = searchParams.get('reason');
    if (gmail === 'connected') {
      pushToast('success', 'Gmail 운영자 계정이 연결되었습니다.');
      setSearchParams({}, { replace: true });
    } else if (gmail === 'error') {
      const msg = reason ? (ERROR_MESSAGES[reason] ?? `연결 실패 (${reason})`) : 'Gmail 연결에 실패했습니다.';
      pushToast('error', msg);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  function handleConnect() {
    window.location.href = '/api/auth/gmail/connect';
  }

  async function saveNaverCredentials() {
    setNaverSaving(true);
    try {
      await api.patch('/settings/naver-credentials', { clientId: naverClientId, clientSecret: naverClientSecret });
      setNaverClientSecret('');
      pushToast('success', '네이버 API 키가 저장되었습니다.');
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setNaverSaving(false);
    }
  }

  async function saveCoupangCredentials() {
    setCoupangSaving(true);
    try {
      await api.patch('/settings/coupang-credentials', {
        accessKey: coupangAccessKey,
        secretKey: coupangSecretKey,
        vendorId: coupangVendorId,
      });
      setCoupangSecretKey('');
      pushToast('success', '쿠팡 API 키가 저장되었습니다.');
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setCoupangSaving(false);
    }
  }

  async function saveAllowedEmails() {
    const emails = emailsText.split(/[\n,]/).map((e) => e.trim().toLowerCase()).filter(Boolean);
    if (emails.length === 0) {
      pushToast('error', '최소 1개 이상의 이메일을 입력해 주세요.');
      return;
    }
    setEmailsSaving(true);
    try {
      await api.patch('/settings/allowed-emails', { emails });
      pushToast('success', `허용 이메일 ${emails.length}개가 저장되었습니다.`);
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setEmailsSaving(false);
    }
  }

  async function saveLogisticsEmail() {
    if (!logisticsEmail.trim()) {
      pushToast('error', '이메일 주소를 입력해 주세요.');
      return;
    }
    setLogisticsSaving(true);
    try {
      await api.patch('/settings/logistics-email', { email: logisticsEmail.trim() });
      pushToast('success', '발주 메일 수신처가 저장되었습니다.');
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setLogisticsSaving(false);
    }
  }

  async function saveDefaultCarrier() {
    setCarrierSaving(true);
    try {
      await api.patch('/settings/default-carrier', { code: carrierCode });
      pushToast('success', '기본 택배사가 저장되었습니다.');
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setCarrierSaving(false);
    }
  }

  return (
    <div className="max-w-2xl">
      <header>
        <p className="label-overline">시스템</p>
        <h1 className="mt-1 text-[28px] font-light tracking-[-0.01em] text-ink-950">설정</h1>
        <p className="mt-1 text-[13px] text-ink-500">시스템 연동 설정을 관리합니다.</p>
      </header>

      {/* Gmail */}
      <section className="mt-8">
        <h2 className="text-base font-medium text-ink-900">Gmail 운영자 계정</h2>
        <p className="mt-1 text-sm text-ink-600">
          발주 메일 발송과 송장 회신 스캔에 사용할 단일 Gmail 계정을 연결합니다.
        </p>
        <div className="mt-4 rounded-lg border border-ink-200 bg-paper p-4 sm:p-6">
          {data.gmail.connected ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden />
                <div className="min-w-0">
                  <p className="break-all text-sm font-medium text-ink-900">{data.gmail.gmailAddress}</p>
                  {data.gmail.updatedAt && (
                    <p className="mt-0.5 text-xs text-ink-500">
                      마지막 연결: {formatDateTime(data.gmail.updatedAt)}
                    </p>
                  )}
                </div>
              </div>
              <button type="button" className="btn-secondary shrink-0 self-start" onClick={handleConnect}>
                <RefreshCw size={14} aria-hidden />
                Google 재연결
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-500" aria-hidden />
              <div className="flex-1">
                <p className="text-sm font-medium text-ink-900">연결된 Gmail 계정이 없습니다.</p>
                <p className="mt-1 text-sm text-ink-600">
                  발주 메일 발송과 송장 회신 스캔을 사용하려면 Gmail 계정을 연결해 주세요.
                </p>
                <button type="button" className="btn-primary mt-4" onClick={handleConnect}>
                  Gmail 연결하기
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Naver API */}
      <section className="mt-8">
        <h2 className="text-base font-medium text-ink-900">네이버 스마트스토어 API</h2>
        <p className="mt-1 text-sm text-ink-600">
          주문 수집과 출고 확정에 사용할 네이버 커머스 API 키를 등록합니다.
        </p>
        <div className="mt-4 rounded-lg border border-ink-200 bg-paper p-4 sm:p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700" htmlFor="naver-client-id">
                Client ID
              </label>
              <input
                id="naver-client-id"
                type="text"
                value={naverClientId}
                onChange={(e) => setNaverClientId(e.target.value)}
                placeholder="Client ID 입력"
                className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700" htmlFor="naver-client-secret">
                Client Secret
                {data.naver.hasSecret && (
                  <span className="ml-2 text-xs text-emerald-600">등록됨 (변경 시 입력)</span>
                )}
              </label>
              <input
                id="naver-client-secret"
                type="password"
                value={naverClientSecret}
                onChange={(e) => setNaverClientSecret(e.target.value)}
                placeholder={data.naver.hasSecret ? '변경하려면 새 값 입력' : 'Client Secret 입력'}
                className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
              />
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={naverSaving || !naverClientId.trim() || !naverClientSecret.trim()}
              onClick={saveNaverCredentials}
            >
              {naverSaving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      </section>

      {/* Coupang Wing API */}
      <section className="mt-8">
        <h2 className="text-base font-medium text-ink-900">쿠팡 Wing API</h2>
        <p className="mt-1 text-sm text-ink-600">
          주문 수집과 송장 등록에 사용할 쿠팡 Wing API 키와 Vendor ID를 등록합니다.
        </p>
        <div className="mt-4 rounded-lg border border-ink-200 bg-paper p-4 sm:p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700" htmlFor="coupang-access-key">
                Access Key
              </label>
              <input
                id="coupang-access-key"
                type="text"
                value={coupangAccessKey}
                onChange={(e) => setCoupangAccessKey(e.target.value)}
                placeholder="Access Key 입력"
                className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700" htmlFor="coupang-secret-key">
                Secret Key
                {data.coupang.hasSecret && (
                  <span className="ml-2 text-xs text-emerald-600">등록됨 (변경 시 입력)</span>
                )}
              </label>
              <input
                id="coupang-secret-key"
                type="password"
                value={coupangSecretKey}
                onChange={(e) => setCoupangSecretKey(e.target.value)}
                placeholder={data.coupang.hasSecret ? '변경하려면 새 값 입력' : 'Secret Key 입력'}
                className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700" htmlFor="coupang-vendor-id">
                Vendor ID
              </label>
              <input
                id="coupang-vendor-id"
                type="text"
                value={coupangVendorId}
                onChange={(e) => setCoupangVendorId(e.target.value)}
                placeholder="A0000###### 형식"
                className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
              />
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={
                coupangSaving ||
                !coupangAccessKey.trim() ||
                !coupangSecretKey.trim() ||
                !coupangVendorId.trim()
              }
              onClick={saveCoupangCredentials}
            >
              {coupangSaving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      </section>

      {/* Allowed emails */}
      <section className="mt-8">
        <h2 className="text-base font-medium text-ink-900">허용 이메일 목록</h2>
        <p className="mt-1 text-sm text-ink-600">
          로그인을 허용할 이메일 주소를 한 줄에 하나씩 입력합니다.
        </p>
        <div className="mt-4 rounded-lg border border-ink-200 bg-paper p-4 sm:p-6">
          <textarea
            rows={5}
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            placeholder="example@gmail.com&#10;admin@company.com"
            className="w-full rounded border border-ink-200 px-3 py-2.5 font-mono text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
          />
          <button
            type="button"
            className="btn-primary mt-3"
            disabled={emailsSaving}
            onClick={saveAllowedEmails}
          >
            {emailsSaving ? '저장 중…' : '저장'}
          </button>
        </div>
      </section>

      {/* Default carrier */}
      <section className="mt-8">
        <h2 className="text-base font-medium text-ink-900">기본 택배사</h2>
        <p className="mt-1 text-sm text-ink-600">
          물류업체 회신에 택배사 정보가 없을 때 사용할 기본 택배사를 선택합니다.
        </p>
        <div className="mt-4 rounded-lg border border-ink-200 bg-paper p-4 sm:p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700" htmlFor="default-carrier">
                택배사
              </label>
              <select
                id="default-carrier"
                value={carrierCode}
                onChange={(e) => setCarrierCode(e.target.value)}
                className="mt-1 w-full rounded border border-ink-200 bg-paper px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
              >
                {data.defaultCarrier.options.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label} ({opt.code})
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={carrierSaving || carrierCode === data.defaultCarrier.code}
              onClick={saveDefaultCarrier}
            >
              {carrierSaving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      </section>

      {/* Logistics email */}
      <section className="mt-8 mb-8">
        <h2 className="text-base font-medium text-ink-900">발주 메일 수신처</h2>
        <p className="mt-1 text-sm text-ink-600">
          발주 엑셀 파일을 보낼 물류업체 이메일 주소를 설정합니다.
        </p>
        <div className="mt-4 rounded-lg border border-ink-200 bg-paper p-4 sm:p-6">
          <input
            type="email"
            value={logisticsEmail}
            onChange={(e) => setLogisticsEmail(e.target.value)}
            placeholder="logistics@company.com"
            className="w-full rounded border border-ink-200 px-3 py-2.5 text-base text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-2 sm:text-sm"
          />
          <button
            type="button"
            className="btn-primary mt-3"
            disabled={logisticsSaving}
            onClick={saveLogisticsEmail}
          >
            {logisticsSaving ? '저장 중…' : '저장'}
          </button>
        </div>
      </section>
    </div>
  );
}
